import type { FC } from "react";
import { useMemo, useState } from "react";

import { useDispatch } from "react-redux";

import type { BacklinksBacklink } from "@similarweb/pro-api-client";
import { Table } from "@similarweb/pro-logic-components";

import { showErrorToast } from "app/actions/toast_actions";
import { allTrackers } from "app/services/track/track";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { useBacklinksApi } from "../../shared/backlinks-api-service";
import { DEFAULT_PAGE_SIZE } from "../../shared/BacklinksConsts";
import { BacklinksBodyContainer } from "../../shared/BacklinksStyledComponents";
import {
    buildBacklinksBacklinksCreateParams,
    getExcelDownloadURL,
    tablePaginationConfig,
    useExpectedBacklinksParams,
} from "../../shared/BacklinksUtils";

import { getBacklinksTableColumns } from "./components/BacklinksTableColumns";
import { BacklinksTableFilters } from "./components/filters/BacklinksTableFilters";

type TableOnSortFunc = Parameters<typeof Table<BacklinksBacklink>>[0]["onSort"];

export const BacklinksTableReport: FC = () => {
    const params = useExpectedBacklinksParams();
    const translate = useTranslation();
    const dispatch = useDispatch();
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE,
    });

    const dataParamsAdapter = buildBacklinksBacklinksCreateParams(params, pagination);

    const { data, isLoading, isFetching, isError } = useBacklinksApi(dataParamsAdapter, params.includeExcludeApiParsed);

    const tableColumns = useMemo(
        () =>
            getBacklinksTableColumns({
                sortField: dataParamsAdapter.sort as string,
                isSortAsc: dataParamsAdapter.asc as boolean,
                translate,
                rowsCount: data?.TotalRecords,
                currentPage: pagination.page,
            }),
        [dataParamsAdapter.sort, dataParamsAdapter.asc, translate, data?.TotalRecords, pagination.page],
    );

    const handleSortingChange: TableOnSortFunc = (sortData) => {
        void proNavigator.updateParams({ asc: sortData.order === "ascend", sort: sortData.field });
        allTrackers.dispatchTrackEvent("Sort", "Click", sortData.field as string);
    };

    const excelUrl = getExcelDownloadURL("backlinksExcel", params);

    const handleExcelClick = () => {
        allTrackers.dispatchTrackEvent(
            "search.backlinks.table.excel.click",
            "click",
            "search.backlinks.table.excel.click",
        );
    };

    const showDataActions = !isError && data?.Records && data.Records.length > 0;

    return (
        <BacklinksBodyContainer>
            <WidgetContainer
                isLoading={false}
                loaderWidth="100%"
                excelUrl={showDataActions ? excelUrl : undefined}
                onExcelClick={handleExcelClick}
                withBorder={true}
                title={<BacklinksTableFilters />}
                headerHeight={82}
                requestBodyForExcel={[...(params.includeExcludeApiParsed ?? [])]}
                downloadProps={{
                    showToast: () => dispatch(showErrorToast(translate("global.download.excel.error"))),
                }}
            >
                <Table
                    size="large"
                    columnConfig={tableColumns}
                    isLoading={isLoading || isFetching}
                    data={data?.Records ?? []}
                    onSort={handleSortingChange}
                    noData={{
                        title: translate("global.nodata.notavilable"),
                        subtitle: translate("global.nodata.notavilable.subtitle"),
                    }}
                    pagination={tablePaginationConfig({
                        ...(data ?? {}),
                        onChange: (_page, _pageSize, nextPage) => {
                            setPagination((prev) => ({ ...prev, page: nextPage }));
                        },
                    })}
                    sticky={{
                        offsetHeader: -32,
                    }}
                />
            </WidgetContainer>
        </BacklinksBodyContainer>
    );
};
